import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import api, { DocumentSignRecipientInput, DocumentSignRecipientResponse } from '@/lib/api';
import { queryKeys } from '@/lib/query-keys';

interface UseRecipientsReturn {
  recipients: DocumentSignRecipientResponse[];
  loading: boolean;
  error: string | null;
  refresh: () => void;
}

export function useRecipients(envelopeId: string | null | undefined): UseRecipientsReturn {
  const { data, isLoading, error, refetch } = useQuery({
    queryKey: envelopeId
      ? queryKeys.documentSign.recipients(envelopeId)
      : ['documentSign-recipients-noop'],
    queryFn: () => {
      if (!envelopeId) return Promise.resolve({ recipients: [] });
      return api.listDocumentSignRecipients(envelopeId);
    },
    enabled: !!envelopeId,
  });

  return {
    recipients: data?.recipients ?? [],
    loading: isLoading,
    error: error instanceof Error ? error.message : error ? 'Failed to load recipients' : null,
    refresh: () => {
      refetch();
    },
  };
}

export function useSetRecipients(envelopeId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (recipients: DocumentSignRecipientInput[]) => {
      const result = await api.setDocumentSignRecipients(envelopeId, { recipients });
      return result.recipients;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.documentSign.recipients(envelopeId) });
      // Replacing recipients cascades to ds_fields rows, so the cached envelope
      // detail (which embeds fields) needs a refresh too.
      queryClient.invalidateQueries({ queryKey: queryKeys.documentSign.envelope(envelopeId) });
    },
  });
}
